import {
  cubeCustomersViewFirstName,
  cubeCustomersViewLastName,
  cubeCustomersViewName,
  cubeCustomersViewPhoneNumber,
  cubeCustomersViewPostalCode,
  cubeCustomersViewRegion,
  cubeCustomersViewUuid,
} from "./cubeCustomersViewModel";
import { fetchCubeLoad, getCubeDataOrThrow } from "./fetchCubeLoad";
import type { CubeFilter, CubeLoadQuery } from "./cubeQueryTypes";

export type CustomerFactRow = {
  uuid: string;
  name: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  postalCode: string;
};

export const buildNoRegionPostalDisplayLabel = (row: CustomerFactRow): string => {
  const nameTrimmed = row.name.trim();
  if (nameTrimmed.length > 0) {
    return nameTrimmed;
  }
  const fromFirstLast = [row.firstName, row.lastName]
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .join(" ")
    .trim();
  if (fromFirstLast.length > 0) {
    return fromFirstLast;
  }
  const phone = row.phoneNumber.trim();
  if (phone.length > 0) {
    return phone;
  }
  return "—";
};

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

const readString = (v: unknown): string => {
  if (typeof v === "string") {
    return v;
  }
  return "";
};

const parseFactRows = (rows: unknown[]): CustomerFactRow[] => {
  const out: CustomerFactRow[] = [];
  for (const item of rows) {
    if (!isRecord(item)) {
      continue;
    }
    out.push({
      uuid: readString(item[cubeCustomersViewUuid]).trim(),
      name: readString(item[cubeCustomersViewName]),
      firstName: readString(item[cubeCustomersViewFirstName]),
      lastName: readString(item[cubeCustomersViewLastName]),
      phoneNumber: readString(item[cubeCustomersViewPhoneNumber]),
      postalCode: readString(item[cubeCustomersViewPostalCode]),
    });
  }
  return out;
};

const noRegionPostalFilters: CubeFilter[] = [
  {
    or: [
      { member: cubeCustomersViewRegion, operator: "notSet" },
      { member: cubeCustomersViewRegion, operator: "equals", values: [""] },
    ],
  },
  { member: cubeCustomersViewPostalCode, operator: "set" },
  { member: cubeCustomersViewPostalCode, operator: "notEquals", values: [""] },
];

export const loadCustomersNoRegionWithPostal = async (params: {
  cubeLoad: (query: CubeLoadQuery) => Promise<unknown>;
}): Promise<CustomerFactRow[]> => {
  const json = await fetchCubeLoad({
    cubeLoad: params.cubeLoad,
    query: {
      dimensions: [
        cubeCustomersViewUuid,
        cubeCustomersViewName,
        cubeCustomersViewFirstName,
        cubeCustomersViewLastName,
        cubeCustomersViewPhoneNumber,
        cubeCustomersViewPostalCode,
      ],
      filters: noRegionPostalFilters,
      order: [[cubeCustomersViewName, "asc"]],
      limit: 5000,
    },
  });
  const rows = getCubeDataOrThrow(json);
  return parseFactRows(rows);
};
